import { type SyntheticEvent, useMemo } from "react";
import {
    getFinanceInsuranceLabelKey,
    goToFinanceInsurance,
    hasInsuranceConfigured,
    showFinanceInsurance,
} from "../../utils/financeInsuranceUtils";
import { type NavStep, NavOrigin } from "../../../shared-logic/utils/Navigation";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../redux/store";
import useServices from "../services/useServices";
import { ServiceTypeEnum } from "../../../../../common-deprecated/types/SettingsType";
import { trackInPageCalculator } from "../../utils/tracking";
import type { CommonFinanceInfoType, PriceType } from "../../../../../common-deprecated/types/CommonTypes";
import { getBnbMonthlyRateFormat } from "../../utils/settings/bnbFinanceSettingUtils";
import { showMobileBasket as showMobileBasketAction } from "../../../../../common-deprecated/redux/actions/UtilActions";
import { useDispatch } from "../../../../../shared-logic/redux/store";

type FinanceInsuranceNavigationTrackingType = "cc-cta" | "cc-action" | "cc-panel" | "cc-footer";

type FinanceInsuranceNavigationEventType = {
    onClick: (event: SyntheticEvent, tracking: FinanceInsuranceNavigationTrackingType, origin?: NavOrigin) => void;
    financeLabelKey: "financeButton" | "financeButtonEdit";
    insuranceLabelKey: "insuranceButton" | "insuranceButtonEdit";
};

export const useFinanceInsuranceNavigation = (step: NavStep): FinanceInsuranceNavigationEventType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const retailerSettings = useBnbSelector((state) => state.retailerSettings);
    const { financeOption, showMobileBasket } = useBnbSelector((state) => state.utils);
    const finance = useBnbSelector((state) => state.finance);
    const { isPersonalizedQuote } = useBnbSelector((state) => state.insuranceRate);
    const financeIsSet = finance.quotes.finance;
    const insuranceIsSet = hasInsuranceConfigured(bnbSettings.requireDefaultInsurance, isPersonalizedQuote);

    const { isServiceConfigured } = useServices();
    const insuranceIsConfigured = isServiceConfigured(ServiceTypeEnum.Insurance);
    const financeIsConfigured = isServiceConfigured(ServiceTypeEnum.Financepacks);

    const financeLabelKey = getFinanceInsuranceLabelKey({
        type: "finance",
        bnbSettings,
        commonSettings,
        quotes: finance.quotes,
    });

    const insuranceLabelKey = getFinanceInsuranceLabelKey({
        type: "insurance",
        bnbSettings,
        commonSettings,
        quotes: finance.quotes,
    });

    return {
        onClick: (
            event: SyntheticEvent,
            tracking: FinanceInsuranceNavigationTrackingType,
            origin: NavOrigin = NavOrigin.FinancialCta,
        ) => {
            const target = NavOrigin.FinancialCta === origin ? "finance" : "insurance";
            const targetConfigured = target === "finance" ? financeIsConfigured : insuranceIsConfigured;
            const trackingLabel =
                financeIsSet || insuranceIsSet
                    ? target === "finance"
                        ? "anchor-edit-finance-calculator"
                        : "anchor-edit-insurance-calculator"
                    : target === "finance"
                      ? "anchor-add-finance-calculator"
                      : "anchor-add-insurance-calculator";

            trackInPageCalculator(tracking, trackingLabel, step);
            if (showMobileBasket) {
                dispatch(showMobileBasketAction(false));
            }
            goToFinanceInsurance(event, {
                step,
                retailerSettings,
                target,
                origin,
                financeOption,
                bnbSettings,
                dispatch,
                targetConfigured,
                quotes: finance.quotes,
            });
        },
        financeLabelKey: financeLabelKey as "financeButton" | "financeButtonEdit",
        insuranceLabelKey: insuranceLabelKey as "insuranceButton" | "insuranceButtonEdit",
    };
};

type FinanceInsuranceReturnType = {
    showInsurance: boolean;
    showFinance: boolean;
    financeInfo: CommonFinanceInfoType & { msgCode?: string };
    monthlyFormat: string;
    totalPrice: PriceType & {
        isExternalPrice: boolean;
        discountTax: number;
        discountWithoutTax: number;
        monthly: number;
        monthlyWithoutPromotion: number;
    };
};

export const useFinanceInsurance = (): FinanceInsuranceReturnType => {
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const car = useBnbSelector((state) => state.car);
    const finance = useBnbSelector((state) => state.finance);
    const { total, totalWithoutPromotions } = finance;
    const modelCode = useBnbSelector((state) => state.car.model.internalCode || "");
    const basketTotal = useBnbSelector((state) => state.basket.total);
    const step = useBnbSelector((state) => state.navigation.step);

    // Financial Information
    const monthlyFormat = getBnbMonthlyRateFormat(commonSettings, modelCode);
    const totalPrice = useMemo(
        () => ({
            ...basketTotal,
            monthly: Number(total),
            monthlyWithoutPromotion: Number(totalWithoutPromotions),
        }),
        [basketTotal, total, totalWithoutPromotions],
    );

    const showFinance = showFinanceInsurance({
        type: "finance",
        bnbSettings,
        car,
        commonSettings,
        finance,
        step,
    });
    const showInsurance = showFinanceInsurance({
        type: "insurance",
        bnbSettings,
        car,
        commonSettings,
        finance,
        step,
    });
    return {
        showFinance,
        showInsurance,
        financeInfo: finance.info,
        monthlyFormat,
        totalPrice,
    };
};
